// Зөвлөгөө (сурагч) — нийтлэг туслахууд: API, Улаанбаатарын цагаар огноо, үе шат, од, аватар.
import React from "react";
import { FiMapPin, FiVideo } from "react-icons/fi";
import { instance } from "utils/axios";

// silent — interceptor-ын "Амжилттай" мэдэгдэл гаргахгүй (өөрсдөө харуулна); алдааг interceptor харуулна
export const api = (method, url, data) => instance({ method, url: `/student/counseling${url}`, data, silent: true }).then((r) => r.data);

// Design System өнгө
export const C = {
  purple: "#5C1090",
  teal: "#00BCD4",
  gold: "#F2CD30",
  tealDeep: "#0E7490",
  cardBg: "#FAF7FF",
  border: "#E5D8F5",
  muted: "#6E5E8A",
};

// ── Огноо: API нь UTC ISO өгнө; Улаанбаатар = UTC+8 (зуны цаг байхгүй) ──
const UB_OFFSET = 8 * 3600 * 1000;
const ub = (v) => new Date((v instanceof Date ? v.getTime() : Date.parse(v)) + UB_OFFSET);
const p2 = (n) => String(n).padStart(2, "0");
const WD_SHORT = ["Ня", "Да", "Мя", "Лх", "Пү", "Ба", "Бя"];
const WD_LONG = ["Ням", "Даваа", "Мягмар", "Лхагва", "Пүрэв", "Баасан", "Бямба"];

export const dayKey = (v) => { const d = ub(v); return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`; };
export const todayKey = () => dayKey(new Date());
export const addDaysKey = (n) => dayKey(new Date(Date.now() + n * 864e5));
export const hm = (v) => { const d = ub(v); return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`; };
export const md = (v) => { const d = ub(v); return `${p2(d.getUTCMonth() + 1)}/${p2(d.getUTCDate())}`; };
export const wdShort = (v) => WD_SHORT[ub(v).getUTCDay()];
export const timeRange = (start, min) => `${hm(start)}–${hm(new Date(Date.parse(start) + (min || 60) * 60000))}`;
// "Даваа, 10-р сарын 26"
export const longDate = (v) => { const d = ub(v); return `${WD_LONG[d.getUTCDay()]}, ${d.getUTCMonth() + 1}-р сарын ${d.getUTCDate()}`; };
// "Өнөөдөр" / "Маргааш" / "Пү 10/29"
export const dayLabel = (v) => {
  const k = dayKey(v);
  if (k === todayKey()) return "Өнөөдөр";
  if (k === addDaysKey(1)) return "Маргааш";
  return `${wdShort(v)} ${md(v)}`;
};
// Хийх ажлын хугацаа (DATEONLY "YYYY-MM-DD")
export const dueLabel = (due) => (due ? `${String(due).slice(5, 10).replace("-", "/")} хүртэл` : "Хугацаагүй");
export const dueSoon = (due) => !!due && String(due).slice(0, 10) <= addDaysKey(7);
export const overdue = (due) => !!due && String(due).slice(0, 10) < todayKey();

export const fullName = (p) => (p ? [p.lastName ? `${p.lastName.charAt(0)}.` : "", p.firstName].filter(Boolean).join(" ") : "");
export const initials = (p) => `${(p?.firstName || "?").charAt(0)}${(p?.lastName || "").charAt(0)}`.toUpperCase();

export const TYPE_LABEL = { online: "Онлайн", in_person: "Биечлэн" };
export const typeText = (m) =>
  m.type === "online" ? "Онлайн · Google Meet" : m.type === "in_person" ? `Биечлэн${m.room ? ` · ${m.room}` : ""}` : "Тодорхойгүй";

export const STAGE = {
  requested: { label: "Хүлээгдэж буй", cls: "bg-[#FFF8DB] text-[#7A5B00] border-[#F2CD30]" },
  upcoming: { label: "Удахгүй", cls: "bg-white text-[#5C1090] border-[#E5D8F5]" },
  live: { label: "Явагдаж байна", cls: "bg-[#00BCD4] text-[#0B2530] border-[#00BCD4]" },
  done: { label: "Дууссан", cls: "bg-[#F3EEFF] text-[#5C1090] border-[#E5D8F5]" },
  cancelled: { label: "Цуцлагдсан", cls: "bg-gray-100 text-gray-500 border-gray-200" },
};

export const TOPICS = ["Их сургуулийн сонголт", "Эссэ / өргөдөл", "Хичээл, сурлага", "Сэтгэл зүй, сэтгэл санаа", "Карьер, мэргэжил", "Бусад"];
export const STAR_WORDS = ["", "Тус болоогүй", "Бага зэрэг тус болсон", "Дунд зэрэг", "Тус болсон", "Маш их тус болсон"];

// ── UI жижиг хэсгүүд ──
export function Badge({ stage, children, className = "" }) {
  const s = STAGE[stage] || STAGE.upcoming;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full border px-2.5 py-0.5 whitespace-nowrap ${s.cls} ${className}`}>
      {stage === "live" && <span className="h-1.5 w-1.5 rounded-full bg-[#0B2530] animate-pulse" />}
      {children || s.label}
    </span>
  );
}

export function Avatar({ person, size = 36, dark }) {
  return (
    <span
      className="rounded-full inline-flex items-center justify-center font-bold shrink-0 select-none"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: dark ? C.tealDeep : "#F3EEFF", color: dark ? "#fff" : C.purple }}
      aria-hidden="true"
    >
      {initials(person)}
    </span>
  );
}

const STAR_PATH = "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2";
export function Star({ on, size = 14, strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={on ? C.purple : "none"} stroke={on ? C.purple : C.muted} strokeWidth={strokeWidth} strokeLinejoin="round" aria-hidden="true">
      <polygon points={STAR_PATH} />
    </svg>
  );
}
export function Stars({ n, size = 14 }) {
  return (
    <span role="img" aria-label={`5-аас ${n || 0} од`} className="inline-flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} on={i <= (n || 0)} size={size} />)}
    </span>
  );
}

export function TypePill({ m }) {
  if (!m.type) return null;
  const Icon = m.type === "online" ? FiVideo : FiMapPin;
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-2.5 py-1 rounded-full border border-[#E5D8F5] bg-[#FAF7FF] text-gray-800 whitespace-nowrap">
      <Icon size={13} />
      {m.type === "online" ? "Онлайн" : `Биечлэн${m.room ? ` · ${m.room}` : ""}`}
    </span>
  );
}

// Хоосон төлөв
export function Empty({ icon: Icon, title, text, children }) {
  return (
    <div className="border border-dashed border-[#E5D8F5] rounded-xl px-5 py-7 flex flex-col items-center text-center gap-2 bg-[#FCFAFF]">
      {Icon && <span className="h-11 w-11 rounded-full bg-[#F3EEFF] text-[#5C1090] flex items-center justify-center mb-1"><Icon size={20} /></span>}
      {title && <div className="text-[15px] font-bold text-gray-900">{title}</div>}
      {text && <div className="text-sm text-[#6E5E8A] max-w-sm leading-relaxed">{text}</div>}
      {children}
    </div>
  );
}

// Шар (golden) үндсэн товч — Design System "accent"
export const goldBtn =
  "inline-flex items-center justify-center gap-2 h-11 px-5 rounded-[10px] bg-[#F2CD30] text-[#1c1c1c] text-[15px] font-bold hover:bg-[#e9c21f] hover:text-[#1c1c1c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
export const outlineBtn =
  "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-[10px] border border-[#E5D8F5] bg-white text-gray-800 text-sm font-semibold hover:bg-[#FAF7FF] hover:text-gray-900 transition-colors disabled:opacity-50";
export const purpleBtn =
  "inline-flex items-center justify-center gap-2 h-10 px-4 rounded-[10px] bg-[#5C1090] text-white text-sm font-bold hover:bg-[#4a0c75] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
